<!-- 日付入力フォーム -->
<script lang="ts" setup>
import styled from 'vue-styled-components';
import { i18n } from '@/i18n';
import { computed, onMounted, ref, watch } from 'vue';
import AppDatePicker from '@/corePagesModule/components/AppDatePicker.vue';
import { CustomStyle } from '@/appLogic/interfaces/style';
import { appendImportantStyle } from '@/utils/display/styleUtil';

// interface

// props
const props = withDefaults(
  defineProps<{
    inputDate?: Date;
    format?: string;
    labelName?: string;
    labelDirection?: string;
    customStyle?: CustomStyle;
    isModal?: boolean;
    disabledDates?: ((date: Date) => boolean) | Date[];
  }>(),
  {
    inputDate: undefined,
    format: 'yyyy-MM-dd',
    labelName: '',
    labelDirection: 'row',
    customStyle: undefined,
    isModal: false,
    disabledDates: () => [],
  }
);

// emits
const emits = defineEmits<{
  (e: 'changeValue', date: Date): void;
}>();
// style variables
// TODO: ここの'row'って何のためにある？
// const Div =
//   props.labelDirection === 'row'
//     ? styled.div`
//         display: grid;
//         align-items: center;
//         grid-template-columns: 8em minmax(0, 2fr);
//         grid-template-rows: minmax(0, 1fr);
//         ${{ ...appendImportantStyle(props?.customStyle?.['Div'] ?? {}) }}
//       `
//     : styled.div`
//         display: grid;
//         align-items: center;
//         grid-template-columns: minmax(0, 1fr);
//         grid-template-rows: repeat(2, minmax(0, 1fr));
//         ${{ ...appendImportantStyle(props?.customStyle?.['Div'] ?? {}) }}
//       `;

const Label = styled.label`
  ${{ ...appendImportantStyle(props?.customStyle?.['Label'] ?? {}) }}
`;
const VueDatePickerComponent = styled.div`
  ${{
    ...appendImportantStyle(props?.customStyle?.['VueDatePickerComponent'] ?? {}),
  }}
`;

//変数
const t = i18n.t;
const locale = computed(() => i18n.locale);

//computed
const date = computed({
  get: () => props.inputDate,
  set: (value) => {
    // 値に変更があると呼ばれるsetter
    if (!value) {
      return;
    }
    emits('changeValue', value);
  },
});

// イベント
onMounted(() => {});

//関数
const isShowLabel = (): boolean => {
  return !!props.labelName;
};
</script>

<template>
  <!-- <div class="date-field-container">
    <label v-if="isShowLabel()" for="example1" class="date-field-container__label">{{ t(labelName) }}</label>
    <div class="date-picker-component date-field-container__date-picker-component"> -->
  <template v-if="isModal">
    <label v-if="isShowLabel()" for="example1" class="date-field-container__label">{{ t(labelName) }}</label>
    <VueDatePickerComponent>
      <AppDatePicker
        v-model="date"
        class="date-field__input"
        :mode-height="1"
        :format="format"
        :enable-time-picker="true"
        :inline="{ input: true }"
        :month-change-on-scroll="false"
        :disabled-dates="props.disabledDates"
        text-input
        auto-apply
        week-start="0"
        :locale="locale"
        :placeholder="t('日付を選択')"
        :show-now-button="true"
        :now-button-label="t('現在を選択')"
      />
    </VueDatePickerComponent>
  </template>
  <div v-else class="date-field-content">
    <label v-if="isShowLabel()" for="example1" class="date-field-container__label">{{ t(labelName) }}</label>
    <div class="date-picker-component date-field-container__date-picker-component">
      <VueDatePickerComponent>
        <AppDatePicker
          v-model="date"
          class="date-field__input"
          :mode-height="1"
          :format="format"
          :enable-time-picker="true"
          :month-change-on-scroll="false"
          week-start="0"
          :locale="locale"
          :placeholder="t('日付を選択')"
          :select-text="t('選択')"
          :cancel-text="t('キャンセル')"
          :show-now-button="true"
          :now-button-label="t('現在を選択')"
          pad
        />
      </VueDatePickerComponent>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dp__input_icon_pad {
  padding-top: 0;
  padding-bottom: 0;
  height: 32px;
}

.date-field-container {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  margin: auto 0;
  max-width: 160px;
  height: 36px;
  font-size: medium;
  &__label {
    display: flex;
    justify-content: flex-start;
    font-weight: bold;
  }
  .date-picker-component {
    display: block;
    width: 100%;
    height: 36px;
    text-align: center;
    font-size: medium;
    border-radius: 6px;
    border-width: 2px;
    border-color: black;
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    &__date-picker {
      height: 100%;
    }
    &:focus {
      outline: 2px solid black;
      opacity: 90%;
    }
    &:disabled {
      cursor: pointer;
    }
  }
}
.date-field-content {
  display: grid;
  align-items: center;
  grid-template-columns: 8em minmax(0, 2fr);
  grid-template-rows: minmax(0, 1fr);
  height: 24px;
  margin-right: auto;
  margin-left: auto;
}

.date-field__input {
  width: v-bind('$props.isModal ? `276px` : `10rem`');
  height: v-bind('$props.isModal ? `55px` : `95%`');
  display: block;
}
</style>

<style lang="scss">
/* - 以下VueDatePickerのCSS操作 - */

@font-face {
  font-family: 'Inter';
  src: url(~@/assets/font/Inter.ttf) format('truetype');
}

/* Inputフィールド全体のレイアウト */
.dp__input {
  width: v-bind('$props.isModal ? `276px` : `10rem`');
  height: v-bind('$props.isModal ? `55px` : `41.8px`');
  display: block;
  padding: 0;
  border: v-bind('$props.isModal ?`1px solid black`:`2px solid black`');
  border-radius: 6px;

  font-family: 'Inter';
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0em;
  text-align: center;
  color: black;
}

/* NOTE: flexにより文字がボタンに収まらない問題を修正する */
.dp__action_buttons {
  flex: none;
}

/* カレンダーアイコンは不要なので削除 */
.dp__input_icon {
  display: none;
}

/* Selectボタンが表示されない問題を修正する */
.dp__action_select {
  background-color: var(--dp-primary-color) !important;
}
</style>
